import { useEffect, useState } from 'react'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Select, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Site, BulkCreateSiteRequest } from '@cmms/types'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

import { api } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'
import { QrCodeDialog, qrPayload } from '../../components/QrCodeDialog'
import { toast } from '../../store/snackbarStore'
import { primary } from '../../theme/palette'
import { MapPicker } from '../../components/MapPicker'
import { CustomerScopePicker } from '../../components/CustomerScopePicker'
import { useCustomerStore } from '../../store/customerStore'
import { formatSiteName } from '../../lib/siteDisplayName'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { usePermissions } from '../../hooks/usePermissions'
import { useSiteFieldLabels } from '../../hooks/useSiteFieldLabels'

export const SITE_COLORS = ['#0D9488', '#D97706', '#0E7490', '#7C3AED', '#EA580C', '#059669', '#2563EB', '#DB2777']

export function SiteDetail({ site, color, onEdit, onDelete }: { site: Site; color: string; onEdit: () => void; onDelete: () => void }) {
  const navigate = useNavigate()
  const { formatDate } = useOrgSettings()
  const { can } = usePermissions()
  const fieldLabels = useSiteFieldLabels()
  const hasCoords = site.latitude != null && site.longitude != null

  // Public-QR sub-state: template picker + QR sticker dialog.
  const orgSlug = useAuthStore((s) => s.user?.org_slug ?? null)
  const [qrOpen, setQrOpen] = useState(false)
  const qc = useQueryClient()

  const templatesQ = useQuery({
    queryKey: ['issue-templates'],
    queryFn: () => api.issueTemplates.list(),
    // Cheap-ish; refetch on window focus is enough.
  })
  const templates = Array.isArray(templatesQ.data) ? templatesQ.data : []
  const activeTemplate = templates.find((t) => t.id === site.issue_template_id) ?? null

  const assignM = useMutation({
    mutationFn: (templateId: string | null) =>
      api.sites.update(site.id, { issue_template_id: templateId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] })
      qc.invalidateQueries({ queryKey: ['issue-templates'] })
      toast.success('Template assignment saved')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  return (
    <motion.div key={site.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26 }}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      <Box sx={{ px: 4, pt: 3.5, pb: 2.5, flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2.5}>
          <Box sx={{ width: 52, height: 52, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${color}14`, color, flexShrink: 0 }}>
            <BusinessOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{formatSiteName(site)}</Typography>
            {site.address && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{site.address}</Typography>
              </Stack>
            )}
            {site.description && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, lineHeight: 1.55 }}>{site.description}</Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
            <Tooltip title={can('sites:update') ? '' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={onEdit} disabled={!can('sites:update')}
                sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider' }}>Edit</Button></Box>
            </Tooltip>
            <Tooltip title={can('sites:delete') ? 'Delete' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><IconButton size="small" onClick={onDelete} disabled={!can('sites:delete')}
                sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, '&:hover': { color: 'error.main' } }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton></Box>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3,
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>

        {hasCoords && (
          <Box sx={{ mb: 3 }}>
            <MapPicker latitude={site.latitude} longitude={site.longitude} height={220}
              onChange={() => {}} />
          </Box>
        )}

        <Sec title="Navigate">
          <Stack direction="row" spacing={1.5}>
            <NavCard icon={<LocationOnOutlinedIcon sx={{ fontSize: 18 }} />} label="Locations" sub="Browse hierarchy" onClick={() => navigate('/locations')} />
            <NavCard icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />} label="Assets" sub="Equipment here" onClick={() => navigate('/assets')} />
            <NavCard icon={<BuildOutlinedIcon sx={{ fontSize: 18 }} />} label="Work Orders" sub="Open tasks" onClick={() => navigate('/work-orders')} />
          </Stack>
        </Sec>

        <Divider />

        {/* ── Public QR / SOS ─────────────────────────────────
             Assign an issue template + generate a printable QR
             sticker for this site. Locations under it inherit
             the template automatically. */}
        <Sec title="Public QR — Report an issue">
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
                Assigned issue template
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Select fullWidth size="small"
                  value={site.issue_template_id ?? ''}
                  disabled={!can('sites:update') || assignM.isPending}
                  onChange={(e) => assignM.mutate(e.target.value === '' ? null : (e.target.value as string))}
                  displayEmpty
                  sx={{ flex: 1 }}
                >
                  <MenuItem value="">
                    <em style={{ color: '#9CA3AF' }}>
                      No template — reporters see a free-text input
                    </em>
                  </MenuItem>
                  {templates.filter((t) => t.is_active).map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <QuestionAnswerOutlinedIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
                        <span>{t.name}</span>
                        <Chip size="small" label={`${t.active_item_count} items`} sx={{
                          height: 18, fontSize: 10, fontWeight: 700,
                          bgcolor: '#3B82F622', color: '#3B82F6',
                        }} />
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              {activeTemplate && (
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.75 }}>
                  Reporters at this site see the {activeTemplate.active_item_count} active item{activeTemplate.active_item_count === 1 ? '' : 's'} from <strong>{activeTemplate.name}</strong>. Configure them from{' '}
                  <Box component="span" onClick={() => navigate('/settings?tab=issue-templates')}
                    sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}>
                    Settings → Issue Templates
                  </Box>.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
                Site QR sticker
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button variant="outlined" size="small" startIcon={<QrCode2OutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setQrOpen(true)}
                  disabled={!orgSlug}
                  sx={{ borderRadius: 2, fontWeight: 600 }}>
                  Print QR
                </Button>
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                  Prints an SOS sticker linked to this site. Scanners without the mobile app see the "Report an issue" form.
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Sec>

        <Divider />

        <Sec title="Details">
          <Stack spacing={1.25}>
            <Kv label="Name" value={site.name} />
            {site.description && <Kv label="Description" value={site.description} />}
            {site.address && <Kv label="Address" value={site.address} />}
            {site.entity && <Kv label="Entity" value={site.entity} />}
            {site.state && <Kv label="State" value={site.state} />}
            {site.branch_area_sqft != null && (
              <Kv label="Branch area" value={`${site.branch_area_sqft.toLocaleString()} sq ft`} />
            )}
            {site.category && <Kv label="Category" value={site.category} />}
            {site.field1 && <Kv label={fieldLabels.field1} value={site.field1} />}
            {site.field2 && <Kv label={fieldLabels.field2} value={site.field2} />}
            {site.field3 && <Kv label={fieldLabels.field3} value={site.field3} />}
            {site.field4 && <Kv label={fieldLabels.field4} value={site.field4} />}
            {site.field5 && <Kv label={fieldLabels.field5} value={site.field5} />}
            {hasCoords && <Kv label="Coordinates" value={`${site.latitude!.toFixed(6)}, ${site.longitude!.toFixed(6)}`} />}
            <Kv label="Created" value={formatDate(site.created_at)} />
          </Stack>
        </Sec>
      </Box>

      {/* Site-level SOS QR sticker. Encodes the public "Report an issue"
          URL; scanners without the mobile app land on the form, users
          with the app deep-link to the site (see Universal / App Links). */}
      {orgSlug && (
        <QrCodeDialog open={qrOpen} onClose={() => setQrOpen(false)}
          payload={qrPayload.siteReport(orgSlug, site.id)}
          title={formatSiteName(site)}
          kind="Site" />
      )}
    </motion.div>
  )
}

export function NavCard({ icon, label, sub, onClick }: { icon: React.ReactNode; label: string; sub: string; onClick: () => void }) {
  return (
    <Box onClick={onClick} sx={{
      flex: 1, px: 2, py: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
      cursor: 'pointer', transition: 'all 120ms ease',
      '&:hover': { borderColor: 'primary.main', bgcolor: primary.hover },
    }}>
      <Box sx={{ color: 'text.secondary', mb: 1 }}>{icon}</Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{sub}</Typography>
    </Box>
  )
}

export function SiteFormDialog({ mode, site, initialCustomerId, onClose, onSubmit, loading }: {
  mode: 'create' | 'edit'; site?: Site
  /** Preselects the customer picker — used when a site is created
   *  contextually (right-click "Add Site" under a Customer node),
   *  taking priority over the globally active switcher customer since
   *  that's a stronger, more specific signal of intent. */
  initialCustomerId?: string
  onClose: () => void
  onSubmit: (data: {
    name: string; description?: string | null; address?: string | null;
    latitude?: number; longitude?: number; customer_id?: string;
    entity?: string | null; state?: string | null;
    branch_area_sqft?: number | null;
    category?: string | null;
    field1?: string | null; field2?: string | null;
    field3?: string | null; field4?: string | null;
    field5?: string | null;
  }) => void
  loading: boolean
}) {
  // Pre-fill customer_id from the active switcher context on create so
  // the common case ("I'm working on Customer X, create a site in X")
  // doesn't need an extra click. On edit, use the site's existing
  // customer_id.
  const activeCustomerId = useCustomerStore((s) => s.customerId)
  const [name, setName] = useState(site?.name ?? '')
  const [desc, setDesc] = useState(site?.description ?? '')
  const [address, setAddress] = useState(site?.address ?? '')
  const [lat, setLat] = useState<number | null>(site?.latitude ?? null)
  const [lng, setLng] = useState<number | null>(site?.longitude ?? null)
  const [customerId, setCustomerId] = useState<string | null>(
    site?.customer_id ?? initialCustomerId ?? activeCustomerId ?? null,
  )
  // Branch meta-data — entity (operating company), state (jurisdiction),
  // branch_area_sqft (built-up area). All optional.
  const [entity, setEntity] = useState(site?.entity ?? '')
  const [stateField, setStateField] = useState(site?.state ?? '')
  const [areaSqft, setAreaSqft] = useState<string>(
    site?.branch_area_sqft != null ? String(site.branch_area_sqft) : '',
  )
  // Customer-configurable site metadata. Category is a free-text bucket
  // (Branch / Warehouse / Datacentre / …); field1..5 are generic
  // placeholder slots the customer will rename meaningfully later. All
  // optional, all free text.
  const [category, setCategory] = useState(site?.category ?? '')
  const [field1, setField1] = useState(site?.field1 ?? '')
  const [field2, setField2] = useState(site?.field2 ?? '')
  const [field3, setField3] = useState(site?.field3 ?? '')
  const [field4, setField4] = useState(site?.field4 ?? '')
  const [field5, setField5] = useState(site?.field5 ?? '')

  // Per-org overrides for the 5 custom-field labels. Blank overrides
  // fall back to "Custom field N" so the form always shows something.
  const fieldLabels = useSiteFieldLabels()

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        {mode === 'create' ? 'Create a new site' : `Edit ${site?.name}`}
      </DialogTitle>
      <DialogContent>
        {mode === 'create' && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
            Sites are the top level — a building complex, campus, or facility. Pin the location on the map.
          </Typography>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <TextField label="Site name" required value={name} onChange={(e) => setName(e.target.value)}
              size="small" fullWidth autoFocus placeholder="e.g. North Warehouse"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            <CustomerScopePicker
              mode="required"
              value={customerId}
              onChange={setCustomerId}
              disabled={mode === 'edit'}
              helperText={mode === 'edit'
                ? "A site's customer can't change after creation — clone the site if you need to move it."
                : 'Sites belong to exactly one customer. Locations + assets + work orders inherit this.'}
            />

            <TextField label="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)}
              size="small" fullWidth multiline minRows={2} placeholder="What is this site used for?"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            {/* Address is editable — picking on the map prefills it,
                but admins can override or type one in directly when
                the map's reverse-geocode is wrong. CSV imports also
                set this field. */}
            <TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)}
              size="small" fullWidth multiline minRows={2}
              placeholder="Street, area, city — or pick on the map"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            {/* Branch meta-data row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Entity (optional)" value={entity} onChange={(e) => setEntity(e.target.value)}
                size="small" fullWidth placeholder="e.g. Bacsys Operations Pvt Ltd"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
              <TextField label="State (optional)" value={stateField} onChange={(e) => setStateField(e.target.value)}
                size="small" fullWidth placeholder="e.g. Tamil Nadu"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
            </Stack>
            <TextField label="Branch area (sq ft, optional)" value={areaSqft}
              onChange={(e) => {
                // Strip anything that isn't a digit so the form can't
                // submit a junk integer the backend would reject.
                setAreaSqft(e.target.value.replace(/[^0-9]/g, ''))
              }}
              size="small" fullWidth placeholder="e.g. 4500"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            {/* ── Category + 5 customer-configurable custom fields ──
                The customer asked for a category bucket + five generic
                slots they can rename later (asset tag, owner, contract
                id, …). Stored as plain text — labels live in the UI,
                column names stay stable so we don't have to migrate
                whenever they change their minds. */}
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: 'text.secondary' }}>
              Category & custom fields
            </Typography>
            <TextField label="Category (optional)" value={category}
              onChange={(e) => setCategory(e.target.value)}
              size="small" fullWidth placeholder="e.g. Branch, Warehouse, Datacentre"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={fieldLabels.field1} value={field1}
                onChange={(e) => setField1(e.target.value)}
                size="small" fullWidth placeholder="Optional"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
              <TextField label={fieldLabels.field2} value={field2}
                onChange={(e) => setField2(e.target.value)}
                size="small" fullWidth placeholder="Optional"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={fieldLabels.field3} value={field3}
                onChange={(e) => setField3(e.target.value)}
                size="small" fullWidth placeholder="Optional"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
              <TextField label={fieldLabels.field4} value={field4}
                onChange={(e) => setField4(e.target.value)}
                size="small" fullWidth placeholder="Optional"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
            </Stack>
            <TextField label={fieldLabels.field5} value={field5}
              onChange={(e) => setField5(e.target.value)}
              size="small" fullWidth placeholder="Optional"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            {lat != null && lng != null && (
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.25 }}>Latitude</Typography>
                  <Typography sx={{ fontSize: 13, fontFamily: 'ui-monospace, monospace' }}>{lat.toFixed(6)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.25 }}>Longitude</Typography>
                  <Typography sx={{ fontSize: 13, fontFamily: 'ui-monospace, monospace' }}>{lng.toFixed(6)}</Typography>
                </Box>
              </Stack>
            )}
          </Stack>

          <Box sx={{ flex: 1.3, minWidth: 0, minHeight: 360 }}>
            <MapPicker
              latitude={lat}
              longitude={lng}
              height={380}
              onChange={(newLat, newLng, newAddr) => {
                setLat(newLat)
                setLng(newLng)
                if (newAddr) setAddress(newAddr)
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || !customerId || loading}
          onClick={() => {
            // Empty-string → null on edit so admins can clear a field;
            // undefined on create so the backend leaves the column null.
            const parsedArea = areaSqft.trim() === '' ? null : parseInt(areaSqft, 10)
            onSubmit({
              name: name.trim(),
              description: desc.trim() || (mode === 'edit' ? null : undefined),
              address: address.trim() || (mode === 'edit' ? null : undefined),
              latitude: lat ?? undefined,
              longitude: lng ?? undefined,
              entity: entity.trim() || (mode === 'edit' ? null : undefined),
              state: stateField.trim() || (mode === 'edit' ? null : undefined),
              branch_area_sqft: Number.isFinite(parsedArea as number)
                ? (parsedArea as number)
                : (mode === 'edit' ? null : undefined),
              category: category.trim() || (mode === 'edit' ? null : undefined),
              field1: field1.trim() || (mode === 'edit' ? null : undefined),
              field2: field2.trim() || (mode === 'edit' ? null : undefined),
              field3: field3.trim() || (mode === 'edit' ? null : undefined),
              field4: field4.trim() || (mode === 'edit' ? null : undefined),
              field5: field5.trim() || (mode === 'edit' ? null : undefined),
              // Only send on create — edits can't reassign customers.
              ...(mode === 'create' && customerId ? { customer_id: customerId } : {}),
            })
          }}
          sx={{ fontSize: 13, fontWeight: 600 }}>
          {loading ? (mode === 'create' ? 'Creating…' : 'Saving…') : (mode === 'create' ? 'Create site' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function Sec({ title, children }: { title: string; children: React.ReactNode }) {
  return <Box sx={{ py: 2.5 }}><Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.25 }}>{title}</Typography>{children}</Box>
}
export function Kv({ label, value }: { label: string; value: string }) {
  return <Stack direction="row" alignItems="flex-start" spacing={1.5}><Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', minWidth: 90 }}>{label}</Typography><Typography sx={{ fontSize: 13, fontWeight: 500 }}>{value}</Typography></Stack>
}

// ─── CSV bulk-import ────────────────────────────────────────────────────────
//
// Self-contained CSV parser + preview dialog. We don't pull in a CSV
// library (papaparse etc.) because the supported shape is tiny — five
// columns, no nested escaping, no Excel quirks worth worrying about
// — and the inline 40-line parser handles all of it. Quoted values
// support embedded commas and doubled-quote escapes; everything else
// is a plain comma-delimited line. UTF-8 BOM is stripped.

interface ParsedRow {
  /** 1-based, matches the user's spreadsheet row (after the header). */
  rowNum: number
  name: string
  description: string
  address: string
  latitude: number | null
  longitude: number | null
  entity: string
  state: string
  branch_area_sqft: number | null
  category: string
  field1: string
  field2: string
  field3: string
  field4: string
  field5: string
  /** Convenience flag — when true the import creates a "Default
   *  Building" location under the new site and (if a checklist name
   *  is provided) attaches the named checklist template to it.
   *  Both columns are optional; when blank the import behaves
   *  exactly as before. */
  default_building: boolean | null
  default_building_checklist: string
  /** Parse-time complaint about this row (e.g. "Missing name"). The
   *  Import button stays disabled while any row has a non-null error. */
  error: string | null
}

const CSV_HEADERS = [
  'name', 'description', 'address', 'latitude', 'longitude',
  'entity', 'state', 'branch_area_sqft',
  'category', 'field1', 'field2', 'field3', 'field4', 'field5',
  'default_building', 'default_building_checklist',
] as const
type CsvField = (typeof CSV_HEADERS)[number]

/// Parse one CSV line into cells. Handles "quoted, values" and "" as
/// an escaped quote inside a quoted value. Anything else is split on
/// commas literally.
function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ } else { inQuotes = false }
      } else {
        cur += ch
      }
    } else {
      if (ch === '"' && cur.length === 0) inQuotes = true
      else if (ch === ',') { out.push(cur); cur = '' }
      else cur += ch
    }
  }
  out.push(cur)
  return out
}

function parseSitesCsv(text: string): { rows: ParsedRow[]; headerError: string | null } {
  // Strip UTF-8 BOM and normalise line endings so Excel-on-Windows
  // (CRLF) and Numbers/Mac (CR) exports both parse cleanly.
  const cleaned = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n')
  const lines = cleaned.split('\n').filter((l) => l.trim().length > 0)
  if (lines.length === 0) {
    return { rows: [], headerError: 'File is empty' }
  }

  const header = parseCsvLine(lines[0]!).map((h) => h.trim().toLowerCase())
  const colIndex: Partial<Record<CsvField, number>> = {}
  CSV_HEADERS.forEach((field) => {
    const idx = header.indexOf(field)
    if (idx >= 0) colIndex[field] = idx
  })
  if (colIndex.name === undefined) {
    return {
      rows: [],
      headerError: `Header row must include a "name" column. Found: ${header.join(', ') || '(empty)'}`,
    }
  }

  const rows: ParsedRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]!).map((c) => c.trim())
    const get = (field: CsvField): string =>
      colIndex[field] !== undefined ? (cells[colIndex[field]!] ?? '') : ''

    const name = get('name')
    const description = get('description')
    const address = get('address')
    const latRaw = get('latitude')
    const lngRaw = get('longitude')
    const entity = get('entity')
    const stateVal = get('state')
    const areaRaw = get('branch_area_sqft')
    const category = get('category')
    const field1 = get('field1')
    const field2 = get('field2')
    const field3 = get('field3')
    const field4 = get('field4')
    const field5 = get('field5')
    const defaultBuildingRaw = get('default_building')
    const defaultBuildingChecklist = get('default_building_checklist')

    let error: string | null = null
    if (!name) error = 'Missing name'
    let latitude: number | null = null
    let longitude: number | null = null
    if (latRaw) {
      const n = Number(latRaw)
      if (Number.isFinite(n)) latitude = n
      else error = error ?? `Latitude "${latRaw}" is not a number`
    }
    if (lngRaw) {
      const n = Number(lngRaw)
      if (Number.isFinite(n)) longitude = n
      else error = error ?? `Longitude "${lngRaw}" is not a number`
    }
    let branchArea: number | null = null
    if (areaRaw) {
      // Strip optional thousand-separators (commas) the user might
      // type/copy from Excel before parsing.
      const cleaned = areaRaw.replace(/[, ]/g, '')
      const n = Number(cleaned)
      if (Number.isFinite(n) && Number.isInteger(n) && n >= 0) branchArea = n
      else error = error ?? `Branch area "${areaRaw}" must be a non-negative whole number`
    }

    // default_building accepts the typical spreadsheet truthy values
    // — TRUE / true / 1 / yes / y → true; FALSE / false / 0 / no / n
    // → false; blank → null (i.e. don't create the location). Anything
    // else is an error so the admin notices their typo instead of
    // silently dropping the side-effect.
    let defaultBuilding: boolean | null = null
    if (defaultBuildingRaw) {
      const v = defaultBuildingRaw.toLowerCase()
      if (['true', '1', 'yes', 'y'].includes(v))      defaultBuilding = true
      else if (['false', '0', 'no', 'n'].includes(v)) defaultBuilding = false
      else error = error ?? `default_building "${defaultBuildingRaw}" must be true or false`
    }
    if (defaultBuildingChecklist && defaultBuilding !== true) {
      error = error ?? 'default_building_checklist requires default_building=true on the same row'
    }

    rows.push({
      rowNum: i + 1, // 1-based + header offset → matches the user's spreadsheet
      name, description, address, latitude, longitude,
      entity, state: stateVal, branch_area_sqft: branchArea,
      category, field1, field2, field3, field4, field5,
      default_building: defaultBuilding,
      default_building_checklist: defaultBuildingChecklist,
      error,
    })
  }
  return { rows, headerError: null }
}

function downloadCsvTemplate() {
  // Two sample rows so the user sees what optional columns look like
  // both filled and empty. Quoted values use a comma in the address
  // to show the escaping rule.
  const csv =
    'name,description,address,latitude,longitude,entity,state,branch_area_sqft,category,field1,field2,field3,field4,field5,default_building,default_building_checklist\n' +
    'Main Plant,Primary manufacturing site,"100 Industrial Rd, Sector 4",12.9716,77.5946,Bacsys Operations,Tamil Nadu,18500,Branch,Region East,RM Alpha,Contract-2026-001,A-Tier,Active,true,Daily Inspection Checklist\n' +
    'Warehouse B,,Old Highway,,,,,,,,,,,,,\n'
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'sites-import-template.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function ImportSitesDialog({
  open, onClose, onImported,
}: { open: boolean; onClose: () => void; onImported: (count: number) => void }) {
  const [filename, setFilename] = useState<string | null>(null)
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [headerError, setHeaderError] = useState<string | null>(null)
  const [submitErrors, setSubmitErrors] = useState<Map<number, string>>(new Map())
  // Default-open on first mount — admins building the import file for
  // the first time want the column guide visible up front. Once they
  // know the shape they can collapse it.
  const [helpOpen, setHelpOpen] = useState(true)

  function reset() {
    setFilename(null); setRows([]); setHeaderError(null); setSubmitErrors(new Map())
  }

  // Reset on close so re-opening the dialog doesn't show stale preview rows.
  useEffect(() => { if (!open) reset() }, [open])

  async function handleFile(file: File) {
    setSubmitErrors(new Map())
    const text = await file.text()
    const parsed = parseSitesCsv(text)
    setFilename(file.name)
    setRows(parsed.rows)
    setHeaderError(parsed.headerError)
  }

  const importM = useMutation({
    mutationFn: (payload: BulkCreateSiteRequest[]) => api.sites.bulkCreate(payload),
    onSuccess: (res) => onImported(res.created),
    onError: (e) => {
      // Server-side per-row validation (422 with `errors: [{row, message}]`)
      // surfaced on ApiError as `.rowErrors` (ApiError has no `.payload`
      // — that was silently discarding every row error) — surface those
      // alongside the preview so the user can pinpoint the bad row in
      // their spreadsheet.
      if (e instanceof ApiError && e.rowErrors?.length) {
        const m = new Map<number, string>()
        // `row` in the server response is 1-based against the payload;
        // we add 1 to align with the user's spreadsheet (which has a
        // header row above their first data row).
        e.rowErrors.forEach((er) => m.set(er.row + 1, er.message))
        setSubmitErrors(m)
        toast.error('Some rows were rejected — see highlights')
        return
      }
      toast.error(e instanceof ApiError ? e.message : 'Import failed')
    },
  })

  const hasClientErrors = rows.length > 0 && rows.some((r) => r.error !== null)
  const canImport = rows.length > 0 && !hasClientErrors && !headerError && !importM.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1 }}>Import sites from CSV</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
          Each row creates one site. The import is all-or-nothing: if any row is
          invalid, no sites are created and you can fix the file and re-run.
          Up to 500 rows per file.
        </Typography>

        {/* Help / guide panel — same expandable pattern the Users
            import uses. Starts open so first-time admins see the
            full column list, an explainer for the new category +
            field1..5 slots, and a copy-pasteable example row. */}
        <Box sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
          mb: 2, overflow: 'hidden',
          bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
        }}>
          <Box onClick={() => setHelpOpen((v) => !v)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.5, py: 1, cursor: 'pointer',
              '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)' },
            }}>
            <HelpOutlineOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
              How to build the CSV
            </Typography>
            {helpOpen
              ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          {helpOpen && (
            <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
              <Stack spacing={2}>
                {/* ── Columns reference ─────────────────────────── */}
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
                    Required column
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    <Box component="code" sx={{
                      bgcolor: (t) => `${t.palette.primary.main}14`, color: 'primary.main',
                      px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, mr: 0.5,
                    }}>name</Box>
                    <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                      — the site's display name. Everything else is optional.
                    </Typography>
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
                    Optional columns
                  </Typography>
                  <Stack spacing={0.75}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {[
                        ['description', 'Free-text — what the site is used for'],
                        ['address',     'Postal address (CSV-escape commas with quotes)'],
                        ['latitude',    'Decimal degrees, e.g. 12.9716'],
                        ['longitude',   'Decimal degrees, e.g. 77.5946'],
                        ['entity',      'Operating company / branch operator'],
                        ['state',       'State / region the site sits in'],
                        ['branch_area_sqft', 'Built-up area, non-negative integer'],
                      ].map(([col, hint]) => (
                        <Stack key={col} direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, mb: 0.5, mr: 1.5 }}>
                          <Box component="code" sx={{
                            bgcolor: 'action.hover', color: 'text.secondary',
                            px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, fontSize: 11.5,
                            whiteSpace: 'nowrap',
                          }}>{col}</Box>
                          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{hint}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>

                {/* ── New: Category + 5 custom fields explainer ── */}
                <Box sx={{
                  p: 1.5, borderRadius: 1.5,
                  bgcolor: (t) => `${t.palette.primary.main}10`,
                  border: '1px solid', borderColor: (t) => `${t.palette.primary.main}30`,
                }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'primary.main', mb: 1 }}>
                    Category & custom fields
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    {['category', 'field1', 'field2', 'field3', 'field4', 'field5'].map((col) => (
                      <Box key={col} component="code" sx={{
                        bgcolor: 'background.paper', color: 'primary.main',
                        px: 0.75, py: 0.2, borderRadius: 0.5, fontWeight: 700, fontSize: 11.5,
                        border: '1px solid', borderColor: (t) => `${t.palette.primary.main}30`,
                      }}>
                        {col}
                      </Box>
                    ))}
                  </Stack>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.55 }}>
                    <strong>category</strong> is a free-text bucket (e.g. <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>Branch</Box>, <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>Warehouse</Box>, <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>Datacentre</Box>).{' '}
                    <strong>field1–field5</strong> are five extra free-text slots you can use today for anything you want to track per site — asset tag, regional manager, contract id, tier, status. The column names stay generic on the database; the labels in the form will be renamed to something meaningful in a future release without you needing to re-import.
                  </Typography>
                </Box>

                {/* ── New: default building + checklist explainer ── */}
                <Box sx={{
                  p: 1.5, borderRadius: 1.5,
                  bgcolor: '#05966910',
                  border: '1px solid', borderColor: '#05966930',
                }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: '#047857', mb: 1 }}>
                    Auto-create a default building + attach a checklist
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    {['default_building', 'default_building_checklist'].map((col) => (
                      <Box key={col} component="code" sx={{
                        bgcolor: 'background.paper', color: '#047857',
                        px: 0.75, py: 0.2, borderRadius: 0.5, fontWeight: 700, fontSize: 11.5,
                        border: '1px solid', borderColor: '#05966930',
                      }}>
                        {col}
                      </Box>
                    ))}
                  </Stack>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.55 }}>
                    Set <strong>default_building</strong> to{' '}
                    <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>true</Box>{' '}
                    (also accepts <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>1</Box>, <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>yes</Box>) and the import will automatically create a location called{' '}
                    <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>Default Building</Box> under that site so you can start logging work against it right away. Fill in{' '}
                    <strong>default_building_checklist</strong> with the exact name of a published checklist from your library and that checklist will be attached to the new building. Leave both cells blank and the import just creates the site, nothing else — your existing CSVs keep working unchanged.
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1, lineHeight: 1.55, fontStyle: 'italic' }}>
                    The checklist name must match a template in your library exactly (case-insensitive). If it's not found, the whole import is rolled back — fix the spelling and re-run.
                  </Typography>
                </Box>

                {/* ── Tips ──────────────────────────────────────── */}
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.55 }}>
                  Column order doesn't matter — the header row drives the
                  mapping. Leave a cell blank to skip a value on that row.
                  CSV-escape any address that contains a comma by wrapping the
                  whole address in <Box component="code" sx={{ bgcolor: 'action.hover',
                    px: 0.5, borderRadius: 0.5, fontSize: 11 }}>"double quotes"</Box>.
                  Hit <strong>Download template</strong> below for a starter
                  file with the exact headers and an example row.
                </Typography>

                {/* ── Example row ───────────────────────────────── */}
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
                    Example row
                  </Typography>
                  <Box sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    p: 1.25, bgcolor: 'background.paper',
                    fontFamily: 'ui-monospace, monospace', fontSize: 11.5,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    color: 'text.secondary',
                  }}>
                    name,description,address,latitude,longitude,entity,state,branch_area_sqft,category,field1,field2,field3,field4,field5,default_building,default_building_checklist{'\n'}
                    Main Plant,Primary manufacturing site,"100 Industrial Rd, Sector 4",12.9716,77.5946,Bacsys Operations,Tamil Nadu,18500,Branch,Region East,RM Alpha,Contract-2026-001,A-Tier,Active,true,Daily Inspection Checklist
                  </Box>
                </Box>
              </Stack>
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={1.25} sx={{ mb: 2 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ fontSize: 13, fontWeight: 600 }}
          >
            {filename ? 'Choose another file' : 'Choose CSV file'}
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) handleFile(f)
                // Allow re-selecting the same filename later.
                e.currentTarget.value = ''
              }}
            />
          </Button>
          <Button
            variant="text"
            startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={downloadCsvTemplate}
            sx={{ fontSize: 13, fontWeight: 600 }}
          >
            Download template
          </Button>
        </Stack>

        {filename && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.25 }}>
            File: <strong>{filename}</strong> · {rows.length} data row{rows.length === 1 ? '' : 's'}
          </Typography>
        )}

        {headerError && (
          <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: 'error.main', color: '#fff' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{headerError}</Typography>
          </Box>
        )}

        {rows.length > 0 && (
          <Box sx={{ maxHeight: 340, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <Box component="thead" sx={{
                position: 'sticky', top: 0, bgcolor: (t) => t.palette.mode === 'light' ? '#F8FAFC' : 'rgba(255,255,255,0.04)',
                '& th': { textAlign: 'left', fontWeight: 700, color: 'text.secondary', px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' },
              }}>
                <tr>
                  <th style={{ width: 56 }}>Row</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Address</th>
                  <th style={{ width: 90 }}>Lat</th>
                  <th style={{ width: 90 }}>Lng</th>
                  <th>Issue</th>
                </tr>
              </Box>
              <Box component="tbody" sx={{
                '& td': { px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'top' },
              }}>
                {rows.map((r) => {
                  const serverErr = submitErrors.get(r.rowNum) ?? null
                  const err = r.error ?? serverErr
                  return (
                    <tr key={r.rowNum} style={err ? { background: 'rgba(220,38,38,0.06)' } : undefined}>
                      <td style={{ color: 'rgba(0,0,0,0.5)', fontFamily: 'ui-monospace, monospace' }}>{r.rowNum}</td>
                      <td style={{ fontWeight: 600 }}>{r.name || <em>—</em>}</td>
                      <td>{r.description || <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td>{r.address || <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td style={{ fontFamily: 'ui-monospace, monospace' }}>{r.latitude ?? <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td style={{ fontFamily: 'ui-monospace, monospace' }}>{r.longitude ?? <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td style={{ color: '#DC2626', fontWeight: err ? 600 : 400 }}>{err ?? ''}</td>
                    </tr>
                  )
                })}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canImport}
          onClick={() => importM.mutate(rows.map((r) => ({
            name: r.name,
            description: r.description || undefined,
            address: r.address || undefined,
            latitude: r.latitude ?? undefined,
            longitude: r.longitude ?? undefined,
            entity: r.entity || undefined,
            state: r.state || undefined,
            branch_area_sqft: r.branch_area_sqft ?? undefined,
            category: r.category || undefined,
            field1: r.field1 || undefined,
            field2: r.field2 || undefined,
            field3: r.field3 || undefined,
            field4: r.field4 || undefined,
            field5: r.field5 || undefined,
            // Only send the two convenience flags when they actually
            // carry a value, so existing imports without these
            // columns continue to round-trip cleanly.
            ...(r.default_building != null
              ? { default_building: r.default_building } : {}),
            ...(r.default_building_checklist
              ? { default_building_checklist: r.default_building_checklist } : {}),
          })))}
          sx={{ fontSize: 13, fontWeight: 600 }}
        >
          {importM.isPending
            ? 'Importing…'
            : `Import ${rows.length} site${rows.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
